import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Clock, Check, Star, Shield,
  Wrench, Droplets, Settings, Zap, Wind, CircleDot
} from 'lucide-react';
import { getActiveServices } from '../api';
import './ServicesPage.css';

/* ── Constants ────────────────────────────────────────────── */
const CATEGORIES = ['All', 'Mechanical', 'Wash & Detail', 'Electrical', 'Tyres & Wheels'];

const ICON_MAP = {
  Wrench:    <Wrench    size={28}/>,
  Droplets:  <Droplets  size={28}/>,
  Settings:  <Settings  size={28}/>,
  Zap:       <Zap       size={28}/>,
  Wind:      <Wind      size={28}/>,
  CircleDot: <CircleDot size={28}/>,
  Shield:    <Shield    size={28}/>,
  Star:      <Star      size={28}/>,
};

/* ── Shimmer skeleton ─────────────────────────────────────── */
function Shimmer({ width = '100%', height = 16, radius = 6, style = {} }) {
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: 'linear-gradient(90deg,var(--bg-secondary) 25%,var(--border) 50%,var(--bg-secondary) 75%)',
      backgroundSize: '200% 100%', animation: 'svcShimmer 1.4s infinite', ...style,
    }}/>
  );
}

function ServiceSkeleton() {
  return (
    <div className="svc-card svc-card--skeleton">
      {/* Image placeholder */}
      <Shimmer height={200} radius={0} style={{ margin: '-32px -32px 24px', width: 'calc(100% + 64px)' }}/>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
        <Shimmer width={56} height={56} radius={10}/>
        <Shimmer width={80} height={18} radius={20}/>
      </div>
      <Shimmer width="65%" height={20} radius={5} style={{ marginBottom:10 }}/>
      <Shimmer height={13} radius={4} style={{ marginBottom:6 }}/>
      <Shimmer width="85%" height={13} radius={4} style={{ marginBottom:6 }}/>
      <Shimmer width="70%" height={13} radius={4} style={{ marginBottom:24 }}/>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:24 }}>
        {[1,2,3,4].map(i => <Shimmer key={i} height={13} radius={4}/>)}
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:20, borderTop:'1px solid var(--border)' }}>
        <Shimmer width={90} height={24} radius={5}/>
        <Shimmer width={110} height={38} radius={8}/>
      </div>
    </div>
  );
}

/* ── Service card image ───────────────────────────────────────
   Priority:
     1. svc.imageUrl  — URL set by admin in the Catalogue panel
     2. /images/services/{slug}.jpg — local file fallback
     3. Icon placeholder — if both fail
───────────────────────────────────────────────────────────── */
function ServiceImage({ svc }) {
  const slug       = (svc.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const primarySrc = svc.imageUrl || null;
  const fallbackSrc= `/images/services/${slug}.jpg`;

  const [src,    setSrc]    = useState(primarySrc || fallbackSrc);
  const [failed, setFailed] = useState(false);

  /* Reset if the service changes */
  useEffect(() => {
    setSrc(primarySrc || fallbackSrc);
    setFailed(false);
  }, [svc.id, svc.imageUrl]); // eslint-disable-line

  if (failed) {
    /* Both URLs failed — render icon placeholder */
    return (
      <div className="svc-card__img-placeholder">
        <div className="svc-card__img-placeholder-icon">
          {ICON_MAP[svc.icon] || <Wrench size={48}/>}
        </div>
        <span className="svc-card__img-placeholder-label">{svc.name}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={svc.name}
      className="svc-card__img"
      loading="lazy"
      onError={() => {
        if (src === primarySrc && primarySrc !== fallbackSrc) {
          /* Primary failed → try local fallback */
          setSrc(fallbackSrc);
        } else {
          /* Both failed → show icon placeholder */
          setFailed(true);
        }
      }}
    />
  );
}

/* ── Main page ────────────────────────────────────────────── */
export default function ServicesPage() {
  const [services,        setServices]        = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [activeCategory,  setActiveCategory]  = useState('All');

  useEffect(() => {
    getActiveServices()
      .then(data => { setServices(data || []); setLoading(false); })
      .catch(()  => setLoading(false));
  }, []);

  const filtered = activeCategory === 'All'
    ? services
    : services.filter(s => s.category === activeCategory);

  /* Derive unique categories from live data and merge with fixed list */
  const liveCategories = ['All', ...new Set(services.map(s => s.category).filter(Boolean))];
  const tabs = CATEGORIES.filter(c => c === 'All' || liveCategories.includes(c))
    .concat(liveCategories.filter(c => c !== 'All' && !CATEGORIES.includes(c)));

  return (
    <div className="services-page" style={{ paddingTop: 72 }}>

      {/* ── Hero ── */}
      <div className="services-hero">
        <div className="container">
          <div className="accent-line" style={{ margin:'0 auto 16px', display:'block' }}/>
          <h1 className="services-hero__title">Our Services</h1>
          <p className="services-hero__subtitle">
            Every service crafted with precision. Every car returned with care.
          </p>
        </div>
      </div>

      {/* ── Filter tabs ── */}
      <div className="services-filter">
        <div className="container">
          <div className="services-filter__tabs">
            {tabs.map(cat => (
              <button
                key={cat}
                className={`services-filter__tab ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Grid ── */}
      <section className="section">
        <div className="container">
          {loading ? (
            <div className="svc-grid">
              {[1,2,3,4,5,6].map(i => <ServiceSkeleton key={i}/>)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="svc-empty">
              <Shield size={44}/>
              <p>No services found in this category.</p>
            </div>
          ) : (
            <div className="svc-grid stagger">
              {filtered.map(svc => (
                <div key={svc.id} className="svc-card animate-fade-up">

                  {/* Promo tag */}
                  {svc.tag && <span className="svc-card__tag">{svc.tag}</span>}

                  {/* ── IMAGE BLOCK ─────────────────────────────────
                      Shows imageUrl from backend (set in Catalogue admin).
                      Falls back to local file then icon placeholder.
                  ────────────────────────────────────────────────── */}
                  <div className="svc-card__img-wrap">
                    <ServiceImage svc={svc}/>
                    {/* Subtle gradient so icon/text below doesn't clash with image */}
                    <div className="svc-card__img-fade"/>
                  </div>

                  {/* Header row: icon + duration */}
                  <div className="svc-card__header">
                    <div className="svc-card__icon">
                      {ICON_MAP[svc.icon] || <Wrench size={28}/>}
                    </div>
                    <div className="svc-card__meta">
                      {svc.duration && (
                        <span className="svc-card__duration">
                          <Clock size={12}/>{svc.duration}
                        </span>
                      )}
                      {svc.rating && (
                        <span className="svc-card__rating">
                          <Star size={12} fill="currentColor"/>
                          {svc.rating}
                        </span>
                      )}
                    </div>
                  </div>

                  <h3 className="svc-card__name">{svc.name}</h3>
                  <p className="svc-card__desc">{svc.description || svc.desc}</p>

                  {/* Includes checklist */}
                  {(svc.includes || []).length > 0 && (
                    <ul className="svc-card__includes">
                      {svc.includes.map(item => (
                        <li key={item}><Check size={12}/>{item}</li>
                      ))}
                    </ul>
                  )}

                  {/* Footer: price + CTA */}
                  <div className="svc-card__footer">
                    <div className="svc-card__price-wrap">
                      <span className="svc-card__price">
                        ₹{Number(svc.price).toLocaleString('en-IN')}
                      </span>
                      <span className="svc-card__price-label">onwards</span>
                    </div>
                    <Link to="/booking" className="btn btn-primary svc-card__book-btn">
                      Book Now <ArrowRight size={14}/>
                    </Link>
                  </div>

                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Guarantee Banner ── */}
      <div className="guarantee-banner">
        <div className="container">
          <div className="guarantee-grid">
            {[
              { icon:<Shield size={24}/>, title:'30-Day Guarantee',  desc:'All services backed by a 30-day workmanship warranty.' },
              { icon:<Star   size={24}/>, title:'OEM Parts Only',    desc:'We only use genuine or OEM-equivalent parts on every job.' },
              { icon:<Clock  size={24}/>, title:'Time Commitments',  desc:'We respect your schedule. Late delivery? We compensate you.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="guarantee-item">
                <div className="guarantee-icon">{icon}</div>
                <div>
                  <h4>{title}</h4>
                  <p>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}